import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  skip,
  switchMap,
  debounceTime,
} from 'rxjs'
//PRIMENG
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CardModule } from 'primeng/card'
import { TabsModule } from 'primeng/tabs'
import { DropdownModule } from 'primeng/dropdown'
import { ChipModule } from 'primeng/chip'
import { TagModule } from 'primeng/tag'
import { BadgeModule } from 'primeng/badge'
import { TooltipModule } from 'primeng/tooltip'
import { ToolbarModule } from 'primeng/toolbar'
import { SkeletonModule } from 'primeng/skeleton'
import { IconFieldModule } from 'primeng/iconfield'
import { DialogModule } from 'primeng/dialog'

//APP
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { ProductsService } from '../../services/products.service'
import { RouterLink } from '@angular/router'
import { environment } from '../../../../../environments/environment'
import { FiltersDialogComponent } from '../filters-dialog/filters-dialog.component'
import { DepartmentService } from '../../../departments/services/department.service'
import { Select } from 'primeng/select'
import { HeatMapComponent } from './heat-map/heat-map.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-products-to-planify',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule,
    TabsModule,
    RouterLink,
    DropdownModule,
    ChipModule,
    TagModule,
    BadgeModule,
    TooltipModule,
    ToolbarModule,
    SkeletonModule,
    FiltersDialogComponent,
    Select,
    FormsModule,
    HeatMapComponent,
    DialogModule,
  ],
  templateUrl: './products-to-planify.component.html',
  styleUrl: './products-to-planify.component.scss',
})
export class ProductsToPlanifyComponent implements OnInit {
  private productsService = inject(ProductsService)

  private tableLazyLoad$ = new BehaviorSubject<TableLazyLoadEvent | null>(null)
  private selectedMlSocietyId = new BehaviorSubject<string | null>(null)

  protected filtersOpened: boolean = false

  // Local storage for products
  protected allProducts: any[] = []
  protected filteredProducts: any[] = []

  protected marketTypes: any[] = []
  protected allDepartements: any[] = []
  protected allRenovators: any[] = []
  protected allAssistants: any[] = []

  public selectedRenovator: string | null = null
  public selectedAssistant: string | null = null

  public selectedMarketType: string | null = ''
  public selectedDepartment: string | null = ''

  // New BehaviorSubjects for local data management
  public productsToPlanify$ = new BehaviorSubject<any[]>([])
  public totalRecords$ = new BehaviorSubject<number>(0)
  public loading$ = this.productsService.loading$

  // Grouped products by market type
  public groupedProducts: {
    marketType: string
    products: any[]
    count: number
    colorIndex: number
  }[] = []

  // Market type filter
  public selectedMarketTypeFilter: string | null = null

  public filtersDialogVisible: boolean = false

  protected societyCounts: Record<string, number> = {}

  public sortOptions = [
    { label: 'Référence ML (A-Z)', value: 'ml_ref_asc' },
    { label: 'Référence ML (Z-A)', value: 'ml_ref_desc' },
    { label: 'Produit (A-Z)', value: 'product_asc' },
    { label: 'Produit (Z-A)', value: 'product_desc' },
    { label: 'Client (A-Z)', value: 'customer_asc' },
    { label: 'Client (Z-A)', value: 'customer_desc' },
  ]

  mlSocieties = Object.values(MlSocieties).map((society) => {
    return {
      id: society,
      label: society,
      icon: 'tll tll-24 fa-logo-' + society,
    }
  })

  public searchInput = new FormControl('')
  public _activeTab: string = this.mlSocieties[0].id
  private initialized = false

  public activeTabIndex: number = 0
  private _previousTab: string | null = null

  // Add property to store column filters
  private columnFilters: Record<string, string> = {}

  get activeTab() {
    return this._activeTab
  }

  set activeTab(value: string) {
    this.selectedMlSocietyId.next(value)
  }

  constructor(private departementsService: DepartmentService) {
    // Only trigger API calls when society changes
    this.selectedMlSocietyId
      .pipe(
        filter((society) => society !== null),
        debounceTime(50),
        takeUntilDestroyed(),
        switchMap((mlSociety) => {
          return this.productsService.getProductToPlanify({
            erpSource: mlSociety!,
          })
        })
      )
      .subscribe((response) => {
        if (response) {
          this.allProducts = response
          this.applyFilters()
        }
      })

    // Set up table event listener for pagination/sorting
    this.tableLazyLoad$
      .pipe(
        filter((event) => event !== null && this.initialized),
        debounceTime(50),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        // Just apply filters locally, no API call
        this.applyFilters()
      })
  }

  ngOnInit() {
    this.initialized = true
    this.initializeData()
    this.loadDepartements()
    this.setFiltersFromLocalStorage()
    this.loadAllSocietyCounts() // Add this line
  }

  loadDepartements() {
    this.departementsService.getAll().subscribe((departements) => {
      this.allDepartements = departements.map((dep) => ({
        label: `${dep.code} - ${dep.name}`,
        value: dep.code,
      }))
    })
  }

  initializeData() {
    const defaultSociety = this.mlSocieties[0].id
    const defaultEvent: TableLazyLoadEvent = { first: 0, rows: 10 }

    this.tableLazyLoad$.next(defaultEvent)
    this.selectedMlSocietyId.next(defaultSociety)

    this.productsService
      .getProductToPlanify({
        erpSource: defaultSociety,
      })
      .subscribe((response) => {
        if (response) {
          this.allProducts = response
          this.filteredProducts = [...this.allProducts]
          this.totalRecords$.next(this.filteredProducts.length)

          console.log(this.allProducts)

          console.log(this.marketTypes)

          const rows = defaultEvent.rows || 10
          this.productsToPlanify$.next(this.filteredProducts.slice(0, rows))

          // Update count for current society
          this.societyCounts[defaultSociety] = response.length
        }
        this.initialized = true
      })

    this.mlSocieties.forEach((society) => {
      this.societyCounts[society.id] = 0
    })
  }

  // Add new method to load counts for all societies
  private loadAllSocietyCounts() {
    this.mlSocieties.forEach((society) => {
      this.productsService
        .getProductToPlanify({
          erpSource: society.id,
        })
        .subscribe((response) => {
          if (response) {
            this.societyCounts[society.id] = response.length
          }
        })
    })
  }

  /**
   * Applies filters and pagination to the data
   */
  public applyFilters() {
    const event = this.tableLazyLoad$.getValue()
    if (!event) return

    // Start with a copy of all products
    let filtered = [...this.allProducts]

    // 🔍 Global search input
    const searchTerm = this.searchInput.getRawValue()?.toLowerCase()
    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          (product.product?.name?.toLowerCase() || '').includes(searchTerm) ||
          (product.affair?.ml_ref?.toLowerCase() || '').includes(searchTerm) ||
          (
            product.affair?.finalsCustomersFullNames?.toLowerCase() || ''
          ).includes(searchTerm) ||
          (product.affair?.workSiteLocality?.toLowerCase() || '').includes(
            searchTerm
          )
      )
    }

    this.marketTypes = Array.from(
      new Set(this.allProducts.map((item) => item.affair.marketType.name))
    ).map((type) => ({
      label: type,
      value: type,
    }))

    this.allRenovators = Array.from(
      new Set(this.allProducts.map((item) => item.agency.name))
    ).map((type) => ({
      label: type,
      value: type,
    }))

    const seen = new Set()

    this.allAssistants = this.allProducts
      .map((item) => item.affair.userAssistant)
      .filter((user) => {
        const fullName = user.firstname + ' ' + user.lastname
        if (seen.has(fullName)) return false
        seen.add(fullName)
        return true
      })
      .map((user) => ({
        label: user.firstname + ' ' + user.lastname,
        value: user.firstname + ' ' + user.lastname,
      }))

    // 🧩 Market type filter (from dropdown select)
    if (this.selectedMarketType) {
      filtered = filtered.filter(
        (item) => item.affair.marketType.name === this.selectedMarketType
      )

      localStorage.setItem('selectedMarketType', this.selectedMarketType)
    }

    if (this.selectedDepartment) {
      filtered = filtered.filter(
        (item) => item.affair.workSiteDepartment === this.selectedDepartment
      )

      localStorage.setItem('selectedDepartment', this.selectedDepartment)
    }

    if (this.selectedRenovator) {
      filtered = filtered.filter(
        (item) => item.agency.name === this.selectedRenovator
      )
    }

    if (this.selectedAssistant) {
      filtered = filtered.filter(
        (item) =>
          item.affair.userAssistant.firstname +
            ' ' +
            item.affair.userAssistant.lastname ===
          this.selectedAssistant
      )
    }

    // 🧹 Column filters (input text fields)
    Object.entries(this.columnFilters).forEach(([field, value]) => {
      if (value) {
        const filterValue = value.toLowerCase()

        if (field === 'address') {
          filtered = filtered.filter((product) => {
            const postalCode = String(
              product.affair?.workSitePostalCode || ''
            ).toLowerCase()
            const locality = String(
              product.affair?.workSiteLocality || ''
            ).toLowerCase()
            return (
              postalCode.includes(filterValue) || locality.includes(filterValue)
            )
          })
        } else {
          filtered = filtered.filter((product) => {
            const fieldValue = this.getNestedValue(product, field)
            if (fieldValue === null || fieldValue === undefined) return false
            return String(fieldValue).toLowerCase().includes(filterValue)
          })
        }
      }
    })

    // 🔃 Sorting
    if (event.sortField) {
      filtered.sort((a, b) => {
        let sortField = Array.isArray(event.sortField)
          ? event.sortField[0]
          : event.sortField

        let valueA, valueB
        if (sortField === 'ml_ref') {
          valueA = a.affair?.ml_ref
          valueB = b.affair?.ml_ref
        } else if (sortField === 'product') {
          valueA = a.product?.name
          valueB = b.product?.name
        } else if (sortField === 'customer') {
          valueA = a.affair?.finalsCustomersFullNames
          valueB = b.affair?.finalsCustomersFullNames
        } else {
          valueA = sortField ? a[sortField] : undefined
          valueB = sortField ? b[sortField] : undefined
        }

        const result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        return event.sortOrder === 1 ? result : -result
      })
    }

    // Store filtered products without market type filter for group counts
    const filteredWithoutMarketType = [...filtered]

    // 🧩 Market type filter (from button filter) - applied AFTER counting
    if (this.selectedMarketTypeFilter) {
      filtered = filtered.filter(
        (item) => item.affair.marketType.name === this.selectedMarketTypeFilter
      )
    }

    // 📊 Update count and visible products
    this.totalRecords$.next(filtered.length)
    this.filteredProducts = filtered

    // Group products by market type (based on all products, counts from filtered without market type)
    this.groupProducts(this.allProducts, filteredWithoutMarketType)

    // 📄 Pagination
    const first = event.first || 0
    const rows = event.rows || 10
    const pagedData = filtered.slice(first, first + rows)

    this.productsToPlanify$.next(pagedData)
  }

  /**
   * Groups products by market type
   * @param allProducts - All products to determine available types
   * @param filteredProducts - Filtered products to count per type
   */
  private groupProducts(allProducts: any[], filteredProducts: any[]) {
    const groupMap = new Map<string, any[]>()

    // First, create groups from all products to have all market types
    allProducts.forEach((product) => {
      const marketType = product.affair?.marketType?.name || 'Non défini'
      if (!groupMap.has(marketType)) {
        groupMap.set(marketType, [])
      }
    })

    // Then, populate with filtered products for count
    const filteredGroupMap = new Map<string, any[]>()
    filteredProducts.forEach((product) => {
      const marketType = product.affair?.marketType?.name || 'Non défini'
      if (!filteredGroupMap.has(marketType)) {
        filteredGroupMap.set(marketType, [])
      }
      filteredGroupMap.get(marketType)!.push(product)
    })

    this.groupedProducts = Array.from(groupMap.keys())
      .map((marketType, index) => ({
        marketType,
        products: filteredGroupMap.get(marketType) || [],
        count: (filteredGroupMap.get(marketType) || []).length,
        colorIndex: index % 5, // Cycle through 5 different colors
      }))
      .sort((a, b) => b.count - a.count) // Sort by count descending
  }

  /**
   * Gets the CSS class for market type header based on index
   */
  getMarketTypeColorClass(colorIndex: number): string {
    const colors = ['color-1', 'color-2', 'color-3', 'color-4', 'color-5']
    return colors[colorIndex] || 'color-1'
  }

  /**
   * Safely gets a nested value from an object using a dot-notation path
   */
  private getNestedValue(obj: any, path: string): any {
    if (!path.includes('.')) return obj[path]
    return path
      .split('.')
      .reduce((prev, curr) => (prev ? prev[curr] : null), obj)
  }

  /**
   * Handles column-specific filtering
   */
  onColumnFilter(event: Event, field: string) {
    const inputElement = event.target as HTMLInputElement
    this.columnFilters[field] = inputElement.value

    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }
  public resetFilters(): void {
    this.selectedDepartment = ''
    this.selectedMarketType = ''

    localStorage.removeItem('selectedMarketType')
    localStorage.removeItem('selectedDepartment')

    this.applyFilters()
  }

  /**
   * Clears all column filters and resets the table
   */
  clearAllFilters() {
    // Clear the column filters object
    this.columnFilters = {}

    // Clear all filter input fields
    const filterInputs = document.querySelectorAll(
      '.p-column-filter'
    ) as NodeListOf<HTMLInputElement>
    filterInputs.forEach((input) => {
      input.value = ''
    })

    // Also clear the global search if there is one
    this.searchInput.setValue('')

    // Reset to first page and apply the cleared filters
    this.tableLazyLoad$.next({
      ...this.tableLazyLoad$.getValue(),
      first: 0,
    })

    this.selectedAssistant = null
    this.selectedRenovator = null

    this.applyFilters()
  }

  lazyLoadEmit(event: TableLazyLoadEvent) {
    if (this.initialized) {
      this.tableLazyLoad$.next(event)
      this.applyFilters()
    }
  }

  getSocietyCount(societyId: string): number {
    return this.societyCounts[societyId] || 0
  }

  refreshData() {
    if (this.initialized) {
      this.tableLazyLoad$.next({ first: 0, rows: 10 })

      // Reload data from API
      this.productsService
        .getProductToPlanify({
          erpSource: this.selectedMlSocietyId.getValue()!,
        })
        .subscribe((response) => {
          if (response) {
            this.allProducts = response || []
            this.applyFilters()

            // Update count for current society
            const currentSociety = this.selectedMlSocietyId.getValue()!
            this.societyCounts[currentSociety as string] = (
              response || []
            ).length
          }
        })

      // Refresh all society counts
      this.loadAllSocietyCounts()
    }
  }

  onSearchChange(event: Event) {
    if (this.initialized) {
      // Update pagination position and apply filters locally
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  onSortChange(event: any) {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
        sortField: event.value.split('_')[0],
        sortOrder: event.value.endsWith('asc') ? 1 : -1,
      })
      this.applyFilters()
    }
  }

  navigateToEros(product: any) {
    const erpSource = this.selectedMlSocietyId.getValue()
    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'affairs/' + product.affair.id + '/show',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'affairs/' + product.affair.id + '/show',
        '_blank'
      )
    }
  }

  navigateToAffair(product: any) {
    window.open(`/affair/${product.affair.mlRef}`, '_blank')
  }

  schedulePlanning(product: any) {
    window.open(`/affair/${product.affair.mlRef}/schedule`, '_blank')
  }

  onActiveIndexChange(event: any) {
    if (!this.initialized) return

    const newTabId = this.mlSocieties[event].id

    if (this._previousTab !== newTabId) {
      this._previousTab = newTabId
      this._activeTab = newTabId

      // This will trigger the API call through the subscription
      this.selectedMlSocietyId.next(newTabId)
      this.tableLazyLoad$.next({ first: 0, rows: 10 })
    }
  }

  setFiltersFromLocalStorage() {
    const marketType = localStorage.getItem('selectedMarketType')
    const department = localStorage.getItem('selectedDepartment')

    if (marketType !== null) {
      this.selectedMarketType = marketType
      this.filtersOpened = true
    }

    if (department !== null) {
      this.selectedDepartment = department
      this.filtersOpened = true
    }

    this.applyFilters()
  }

  isMapOpened: boolean = false

  onDepartmentClicked(code: string) {
    this.selectedDepartment = code
    this.isMapOpened = false // Close modal when department is selected
    this.filtersOpened = true // Open advanced filters to show the applied department filter
    this.applyFilters()
  }

  /**
   * Gets the row color class based on market type
   */
  getRowColorClass(marketType: string): string {
    const group = this.groupedProducts.find((g) => g.marketType === marketType)
    if (!group) return ''
    return `market-type-row-${this.getMarketTypeColorClass(group.colorIndex)}`
  }

  /**
   * Gets the tag severity for market type
   */
  getMarketTypeSeverity(
    marketType: string
  ):
    | 'info'
    | 'success'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | undefined {
    return 'info'
  }

  /**
   * Gets custom styles for market type tag
   */
  getMarketTypeTagStyle(marketType: string): any {
    const group = this.groupedProducts.find((g) => g.marketType === marketType)
    if (!group) return {}

    const colors = [
      { bg: '#3b82f6', text: '#ffffff' },
      { bg: '#10b981', text: '#ffffff' },
      { bg: '#f59e0b', text: '#ffffff' },
      { bg: '#8b5cf6', text: '#ffffff' },
      { bg: '#ef4444', text: '#ffffff' },
    ]

    const color = colors[group.colorIndex] || colors[0]
    return {
      'background-color': color.bg,
      color: color.text,
    }
  }

  /**
   * Filters products by market type
   */
  filterByMarketType(marketType: string | null) {
    this.selectedMarketTypeFilter = marketType
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  /**
   * Gets the total count of all products (sum of all market types)
   */
  getTotalCount(): number {
    return this.groupedProducts.reduce((sum, group) => sum + group.count, 0)
  }
}
